"use client"

import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { ArrowLeft, BarChart3, AlertTriangle, PackageX, Clock } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { API_ENDPOINTS } from "@/constants/config"

const InventoryReports = () => {
  const navigate = useNavigate()
  const [inventory, setInventory] = useState([])
  const [reorderAlerts, setReorderAlerts] = useState([])
  const [nonMovingItems, setNonMovingItems] = useState([])
  const [nonMovingDays, setNonMovingDays] = useState(90)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  const fetchReportsData = async () => {
    try {
      setLoading(true)
      setError("")

      const token = localStorage.getItem("token")
      if (!token) throw new Error("Authentication token not found")

      const [inventoryRes, reorderRes, nonMovingRes] = await Promise.all([
        fetch(`${API_ENDPOINTS.inventory}?activeStatus=all&paginate=false&includeHistory=true&sortBy=item_name&sortOrder=asc`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(API_ENDPOINTS.inventoryReorderAlerts, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_ENDPOINTS.inventoryNonMoving}?days=${nonMovingDays}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ])

      if (!inventoryRes.ok) {
        throw new Error(`Failed to load inventory report data: ${inventoryRes.statusText}`)
      }

      const inventoryData = await inventoryRes.json()
      const reorderData = reorderRes.ok ? await reorderRes.json() : { data: [] }
      const nonMovingData = nonMovingRes.ok ? await nonMovingRes.json() : { data: [] }

      setInventory(inventoryData.data || [])
      setReorderAlerts(reorderData.data || [])
      setNonMovingItems(nonMovingData.data || [])
    } catch (err) {
      setError(err.message || "Failed to load report data")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchReportsData()
  }, [nonMovingDays])

  // weighted_avg_cost is computed server-side (see WEIGHTED_AVG_COST_EXPR in
  // inventoryController.js) so the full purchase_history never has to be shipped
  // to the client just to derive this one number.
  const getWeightedAvgCost = (item) => Number(item.weighted_avg_cost) || 0

  const metrics = useMemo(() => {
    const lowStockCount = inventory.filter(
      (i) => i.status === "Low Stock" || i.status === "Out of Stock"
    ).length

    const outOfStockCount = inventory.filter(
      (i) => i.status === "Out of Stock" || Number(i.quantity_available || 0) <= 0
    ).length

    const totalAvailableUnits = inventory.reduce((sum, item) => sum + (Number(item.quantity_available) || 0), 0)
    const totalOnHoldUnits = inventory.reduce((sum, item) => sum + (Number(item.on_hold_quantity) || 0), 0)

    const totalStockValue = inventory.reduce(
      (sum, item) => sum + (Number(item.quantity_available) || 0) * getWeightedAvgCost(item),
      0
    )

    const categoryReport = Object.values(
      inventory.reduce((acc, item) => {
        const category = item.category || "Uncategorized"
        if (!acc[category]) {
          acc[category] = { category, items: 0, quantity: 0, value: 0 }
        }
        acc[category].items += 1
        acc[category].quantity += Number(item.quantity_available) || 0
        acc[category].value += (Number(item.quantity_available) || 0) * getWeightedAvgCost(item)
        return acc
      }, {})
    )
      .sort((a, b) => b.value - a.value)
      .slice(0, 8)

    const vendorReport = Object.values(
      inventory.reduce((acc, item) => {
        const vendorName = item.vendor_name || "Unassigned"
        if (!acc[vendorName]) {
          acc[vendorName] = { vendorName, items: 0, quantity: 0, value: 0 }
        }
        acc[vendorName].items += 1
        acc[vendorName].quantity += Number(item.quantity_available) || 0
        acc[vendorName].value += (Number(item.quantity_available) || 0) * getWeightedAvgCost(item)
        return acc
      }, {})
    )
      .sort((a, b) => b.value - a.value)
      .slice(0, 8)

    return {
      lowStockCount,
      outOfStockCount,
      totalAvailableUnits,
      totalOnHoldUnits,
      totalStockValue,
      categoryReport,
      vendorReport,
    }
  }, [inventory])

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(Number(amount) || 0)

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center h-[60vh]">
        <div className="flex items-center gap-2 text-gray-600">
          <Clock className="h-5 w-5 animate-spin text-[#0263cc]" />
          <span>Loading reports...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full mx-auto space-y-4">
      <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
        <CardHeader className="bg-[#0263cc]/5 border-b border-[#0263cc]/10">
          <CardTitle className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-[#0263cc]">
              <BarChart3 className="h-5 w-5" />
              <span>Inventory Reports</span>
            </div>
            <div className="flex items-center gap-2">
              <Select value={String(nonMovingDays)} onValueChange={(v) => setNonMovingDays(Number(v))}>
                <SelectTrigger className="w-[140px] bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                  <SelectItem value="180">180 days</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={() => navigate("/dashboard/inventory/analytics")} className="flex items-center gap-1.5">
                <ArrowLeft className="h-4 w-4" />
                Back to Analytics
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 md:p-6 space-y-5">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-3">
            <Card className="border border-[#0263cc]/10 shadow-sm xl:col-span-2">
              <CardContent className="p-4">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Stock Value</div>
                <div className="text-xl font-bold text-[#0263cc] mt-1">{formatCurrency(metrics.totalStockValue)}</div>
              </CardContent>
            </Card>
            <Card className="border border-slate-200 shadow-sm">
              <CardContent className="p-4">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total Items</div>
                <div className="text-xl font-bold text-slate-700 mt-1">{inventory.length}</div>
              </CardContent>
            </Card>
            <Card className="border border-amber-200 shadow-sm">
              <CardContent className="p-4">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Low Stock</div>
                <div className="text-xl font-bold text-amber-700 mt-1">{metrics.lowStockCount}</div>
              </CardContent>
            </Card>
            <Card className="border border-red-200 shadow-sm">
              <CardContent className="p-4">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Out of Stock</div>
                <div className="text-xl font-bold text-red-700 mt-1">{metrics.outOfStockCount}</div>
              </CardContent>
            </Card>
            <Card className="border border-slate-200 shadow-sm">
              <CardContent className="p-4">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Reorder Alerts</div>
                <div className="text-xl font-bold text-slate-700 mt-1">{reorderAlerts.length}</div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Card className="border border-slate-200 shadow-sm">
              <CardContent className="p-4">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Available Units</div>
                <div className="text-xl font-bold text-slate-700 mt-1">{metrics.totalAvailableUnits}</div>
              </CardContent>
            </Card>
            <Card className="border border-slate-200 shadow-sm">
              <CardContent className="p-4">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">On Hold Units</div>
                <div className="text-xl font-bold text-amber-700 mt-1">{metrics.totalOnHoldUnits}</div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <Card className="border border-gray-200 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Top Categories by Value</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {metrics.categoryReport.length === 0 ? (
                  <div className="text-sm text-gray-500">No category report data available.</div>
                ) : (
                  metrics.categoryReport.map((row) => (
                    <div key={row.category} className="flex items-center justify-between border-b border-gray-100 pb-2">
                      <div>
                        <div className="font-medium text-sm">{row.category}</div>
                        <div className="text-xs text-gray-500">{row.items} items • {row.quantity} units</div>
                      </div>
                      <div className="text-sm font-semibold text-[#0263cc]">{formatCurrency(row.value)}</div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            <Card className="border border-gray-200 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Top Vendors by Value</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {metrics.vendorReport.length === 0 ? (
                  <div className="text-sm text-gray-500">No vendor report data available.</div>
                ) : (
                  metrics.vendorReport.map((row) => (
                    <div key={row.vendorName} className="flex items-center justify-between border-b border-gray-100 pb-2">
                      <div>
                        <div className="font-medium text-sm">{row.vendorName}</div>
                        <div className="text-xs text-gray-500">{row.items} items • {row.quantity} units</div>
                      </div>
                      <div className="text-sm font-semibold text-[#0263cc]">{formatCurrency(row.value)}</div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Card className="border border-orange-200 shadow-sm">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Reorder Alert Items</div>
                  <div className="text-xl font-bold text-orange-700 mt-1">{reorderAlerts.length}</div>
                </div>
                <AlertTriangle className="h-6 w-6 text-orange-500" />
              </CardContent>
            </Card>
            <Card className="border border-slate-200 shadow-sm">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Non-Moving ({nonMovingDays}d)</div>
                  <div className="text-xl font-bold text-slate-700 mt-1">{nonMovingItems.length}</div>
                </div>
                <PackageX className="h-6 w-6 text-slate-500" />
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default InventoryReports
